import { authStore } from './authStore';
import { refreshStore } from './cognito';
import { getAuthToken } from './authToken';

/**
 * Expired / revoked / rotated credential handling.
 *
 * THE SECURITY HALF: until this existed, a 401 left the stored token in
 * localStorage and the console kept sending it. That is worst on the incident
 * path — `POST /admin/users/{ref}/rotate-token` exists precisely so a
 * compromised credential can be replaced, and the moment you most need the old
 * one to stop working was the moment it did not.
 *
 * `notifyAuthExpired` therefore clears the store FIRST and unconditionally,
 * before any React state is involved. A 401 that arrives before the provider
 * mounts, or after it unmounts, still revokes the credential locally — the
 * clearing must not depend on a listener being registered.
 *
 * The listener is only how the UI catches up: AuthProvider registers one so the
 * in-memory session follows, which lets `RequireAdvisor` route to /sign-in the
 * normal way rather than this module reaching for the router or a hard reload.
 */

type ExpiryListener = () => void;

let listener: ExpiryListener | null = null;
/** Everyone else who must follow a wipe (App clears the query cache), however it came. */
const wiped = new Set<() => void>();

export function onSessionWiped(fn: () => void): () => void {
  wiped.add(fn);
  return () => { wiped.delete(fn); };
}

/** Everyone who must catch up when a session comes back from a blip (App refetches what
 *  failed while Cognito could not be reached). */
const renewed = new Set<() => void>();

export function onSessionRenewed(fn: () => void): () => void {
  renewed.add(fn);
  return () => { renewed.delete(fn); };
}

export function notifySessionRenewed(): void {
  for (const fn of renewed) fn();
}

/** Registered by AuthProvider. Returns an unsubscribe for StrictMode remounts. */
export function onAuthExpired(fn: ExpiryListener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

/** Reason for the bounce, so /sign-in can say why rather than looking like a
 *  random logout. sessionStorage, not local: it should not outlive the tab. */
const REASON_KEY = 'bioweave_console_signout_reason';

export type SignOutReason = 'expired';

/**
 * PURE read. Deliberately not read-and-consume.
 *
 * A consuming reader is a side effect, and React calls render, state
 * initialisers and effects more than once — StrictMode double-invokes all three
 * to surface exactly this. A read-and-clear in an initialiser returned the
 * reason on the first call and null on the second, so the notice vanished in
 * development and would have appeared in production: the same code behaving
 * differently in the two places, which is worse than it never working.
 *
 * The reason is cleared explicitly by `clearSignOutReason` when the operator
 * signs in again — the event that actually resolves it.
 */
export function peekSignOutReason(): SignOutReason | null {
  const v = globalThis.sessionStorage?.getItem(REASON_KEY) ?? null;
  return v === 'expired' ? 'expired' : null;
}

export function clearSignOutReason(): void {
  globalThis.sessionStorage?.removeItem(REASON_KEY);
}

/**
 * Called for any 401/403 from any API layer. Idempotent — several in-flight
 * queries failing together must not produce several sign-outs.
 *
 * `live`: the provider holds a session in memory with nothing stored behind it (a
 * Cognito session with no refresh token), so the wipe must run even though the stores
 * are empty.
 */
export function notifyAuthExpired(opts: { live?: boolean } = {}): void {
  // Nothing stored means we have already handled this (or were never signed
  // in), so a burst of parallel 401s collapses to one notification.
  // A Cognito session keeps only its refresh token at rest (the access token is in
  // memory), so either credential being present means there is something to revoke.
  if (!opts.live && !authStore.load() && !refreshStore.load()) return;
  authStore.clear();
  refreshStore.clear();
  globalThis.sessionStorage?.setItem(REASON_KEY, 'expired');
  listener?.();
  for (const fn of wiped) fn();
}

// --- a Cognito session tries one refresh before it is wiped (Tiny's D2, L1, L2) ---

/**
 * The access token of a Cognito session lasts an hour. A laptop asleep past that comes
 * back with an expired access token and a still-good refresh token, and the first focus
 * refetch 401s before the refresh timer runs. So for a Cognito session (the provider
 * registers a recovery only while the session signed in through Cognito), a 401 first
 * asks for one refresh.
 *
 * RECOVERED means more than "a new token came back": the provider's recovery also has
 * core ACCEPT the new access token, so a core that refuses every token (a pool key
 * rotation it has not seen, a client id it no longer lists) ends the session instead of
 * refetching forever (Tiny's L1: 460 requests a second from one tab).
 *
 *   'recovered'  refreshed and accepted: the caller refetches
 *   'stale'      the request left with a token this tab has since replaced (a recovery, a
 *                pasted token): the session is not in question, the caller refetches
 *   'kept'       nothing to do now: a blip (the provider retries), a stale recovery (a
 *                newer session owns the state), a 401 on the current token just after a
 *                recovery, or core accepting the current token (the 401 was not about
 *                it). The caller does NOT refetch (L2).
 *   'ended'      the session is over (notifyAuthExpired ran): the caller clears
 *   'joined'     another request's recovery, in flight, recovered: it refetches for
 *                everyone, so this caller does nothing
 *
 * Bounded: one recovery in flight for a burst of 401s, and at most MAX_PER_MINUTE
 * recoveries a minute; past that the session ends rather than spend rotations in a loop.
 */
export type RecoveryOutcome = 'recovered' | 'kept' | 'ended';
export type FailureOutcome = RecoveryOutcome | 'stale' | 'joined';
type Recovery = () => Promise<RecoveryOutcome>;
let recovery: Recovery | null = null;
let inflight: Promise<RecoveryOutcome> | null = null;
let recentAt: number[] = [];
const AFTER_RECOVERY_MS = 5_000;
const MAX_PER_MINUTE = 3;

/** Registered by AuthProvider while its session signed in through Cognito. */
export function onAuthRecover(fn: Recovery): () => void {
  recovery = fn;
  return () => {
    if (recovery === fn) recovery = null;
  };
}

/** Tests only: forget the recovery history. */
export function resetRecoveryForTests(now: () => number = Date.now): void {
  recentAt = [];
  inflight = null;
  clock = now;
}
let clock: () => number = Date.now;

/** For any 401 from the API layer. `sentWith` is the bearer the refused request carried
 *  (tokenSentWith); undefined when it was not recorded. Only a 401 on the CURRENT token is
 *  about this session: the window and the cap count those alone (Tiny's follow-up 1). */
export async function handleAuthFailure(sentWith?: string | null): Promise<FailureOutcome> {
  const current = getAuthToken();
  if (sentWith !== undefined && current !== null && sentWith !== current) return 'stale';
  if (!recovery) {
    notifyAuthExpired();
    return 'ended';
  }
  // A burst of 401s shares the recovery in flight. Only the request that STARTED it is
  // told 'recovered' (and refetches for everyone); the rest are 'joined', or the shared
  // 'kept' / 'ended'. Told 'recovered', each waiter refetched every query again: one
  // expired token on wake gave 154 requests (auth-recovery-refetches-once-per-waiter).
  if (inflight) {
    const shared = await inflight;
    return shared === 'recovered' ? 'joined' : shared;
  }
  const now = clock();
  recentAt = recentAt.filter((t) => now - t < 60_000);
  const last = recentAt[recentAt.length - 1];
  if (last !== undefined && now - last < AFTER_RECOVERY_MS) return 'kept';
  if (recentAt.length >= MAX_PER_MINUTE) {
    notifyAuthExpired();
    return 'ended';
  }
  const run = recovery();
  inflight = run;
  try {
    const outcome = await run;
    if (outcome === 'recovered') recentAt.push(clock());
    return outcome;
  } finally {
    inflight = null;
  }
}
